import { blockInsertEntitySchema } from "./model";
import type {
  BlockChildEntity,
  BlockDefinition,
  BlockInsertEntity,
  DrawingDocument,
  DrawingEntity,
  DrawingSpace,
} from "./model";

export interface EntityLocation {
  spaceIndex: number;
  entityIndex: number;
  space: DrawingSpace;
  entity: DrawingEntity;
}

export type EntityUpdater = (
  entity: DrawingEntity,
) => DrawingEntity | null | undefined;

export function getSpaceById(
  document: DrawingDocument,
  spaceId: string,
): DrawingSpace | null {
  return document.spaces.find((space) => space.id === spaceId) ?? null;
}

export function findEntityLocationById(
  document: DrawingDocument,
  entityId: string,
): EntityLocation | null {
  for (
    let spaceIndex = 0;
    spaceIndex < document.spaces.length;
    spaceIndex += 1
  ) {
    const space = document.spaces[spaceIndex];

    if (!space) {
      continue;
    }

    const entityIndex = space.entities.findIndex(
      (entity) => entity.id === entityId,
    );

    if (entityIndex === -1) {
      continue;
    }

    const entity = space.entities[entityIndex];

    if (!entity) {
      continue;
    }

    return {
      spaceIndex,
      entityIndex,
      space,
      entity,
    };
  }

  return null;
}

export function getEntityById(
  document: DrawingDocument,
  entityId: string,
): DrawingEntity | null {
  return findEntityLocationById(document, entityId)?.entity ?? null;
}

export function updateEntityById(
  document: DrawingDocument,
  entityId: string,
  updater: EntityUpdater,
): DrawingDocument {
  const location = findEntityLocationById(document, entityId);

  if (!location) {
    return document;
  }

  const nextEntity = updater(location.entity);

  if (!nextEntity || nextEntity === location.entity) {
    return document;
  }

  if (nextEntity.id !== location.entity.id) {
    throw new Error(
      `Entity id updates are not supported by updateEntityById: ${location.entity.id} -> ${nextEntity.id}`,
    );
  }

  if (nextEntity.spaceId !== location.space.id) {
    throw new Error(
      `Entity spaceId updates are not supported by updateEntityById: ${location.entity.id} -> ${nextEntity.spaceId}`,
    );
  }

  const nextSpaces = document.spaces.slice();
  const nextEntities = location.space.entities.slice();
  nextEntities[location.entityIndex] = nextEntity;
  nextSpaces[location.spaceIndex] = {
    ...location.space,
    entities: nextEntities,
  };

  return {
    ...document,
    spaces: nextSpaces,
  };
}

export function patchEntityById(
  document: DrawingDocument,
  entityId: string,
  patch: Partial<DrawingEntity>,
): DrawingDocument {
  return updateEntityById(
    document,
    entityId,
    (entity) =>
      ({
        ...entity,
        ...patch,
        id: entity.id,
        spaceId: entity.spaceId,
      }) as DrawingEntity,
  );
}

// ═══════════════════════════════════════════════════════════════
// Block-edit session helpers (Phase 2.3 — BEDIT / BSAVE / BCLOSE)
// ═══════════════════════════════════════════════════════════════

/**
 * Unique id for the synthetic block-edit space. Only one BEDIT session
 * can be active at a time (AutoCAD parity: the Block Editor pins all
 * drawing-area focus on a single block at once). We reuse the id on
 * every enter-block-edit call so selection / ui state tied to the
 * space id stays stable across repeated edits.
 */
export const BLOCK_EDIT_SPACE_ID = "__block-edit__";

/**
 * Enter a BEDIT session for the given block. Behaviour mirrors
 * AutoCAD's BEDIT command exactly:
 *   1. Snapshot the current block definition as a JSON string (used by
 *      BCLOSE / discard to restore the original state).
 *   2. Upsert a synthetic block-edit space with id BLOCK_EDIT_SPACE_ID
 *      whose `blockEditName`, `blockEditSnapshot`, and
 *      `blockEditPreviousSpaceId` fields are populated.
 *   3. Activate the synthetic space.
 *
 * When `createIfMissing` is true and the block does not yet exist in
 * `document.blocks`, an empty BlockDefinition is created with a
 * (0,0,0) base point — the user can author geometry inside BEDIT and
 * later persist it via BSAVE. When the block already exists, the
 * existing BlockDefinition is preserved verbatim and the snapshot
 * captures its current state.
 *
 * The calling reducer validates `blockName` (reserved names,
 * forbidden characters, length ≤255) *before* invoking this helper.
 */
export function enterBlockEditSpace(
  document: DrawingDocument,
  blockName: string,
  options: { createIfMissing: boolean } = { createIfMissing: false },
): DrawingDocument {
  const existing = document.blocks[blockName];
  if (!existing && !options.createIfMissing) {
    throw new Error(`Block "${blockName}" does not exist`);
  }

  // Seed a fresh BlockDefinition if the caller opted in. The handle /
  // ownerHandle / xrefPathName are left blank — the DXF exporter fills
  // them on the way out (same lifecycle as BLOCK-command authored
  // blocks).
  const nextBlockDef: BlockDefinition = existing ?? {
    name: blockName,
    handle: "",
    layer: "0",
    flags: 0,
    basePoint: { x: 0, y: 0, z: 0 },
    xrefPathName: "",
    description: "",
    entities: [],
    // Authoring path — BEDIT-created blocks are static by default; the
    // user must attach parameters/actions explicitly for them to become
    // dynamic (Track B + DYN-1.5).
    dynamicBlock: false,
    xrefStatus: "resolved",
  };

  const snapshot = JSON.stringify(nextBlockDef);

  const previousSpaceId = document.activeSpaceId;

  // Drop any prior block-edit space before pushing the new one — AutoCAD
  // does not allow two BEDIT sessions simultaneously, so we simply
  // replace the previous session. A warning is emitted at the reducer
  // level.
  const spacesWithoutBlockEdit = document.spaces.filter(
    (space) => space.kind !== "block-edit",
  );

  const blockEditSpace: DrawingSpace = {
    id: BLOCK_EDIT_SPACE_ID,
    kind: "block-edit",
    name: `Block Editor: ${blockName}`,
    entities: [],
    blockEditName: blockName,
    blockEditSnapshot: snapshot,
    blockEditPreviousSpaceId: previousSpaceId,
    refeditWorkingSet: [],
  };

  return {
    ...document,
    blocks: existing
      ? document.blocks
      : { ...document.blocks, [blockName]: nextBlockDef },
    spaces: [...spacesWithoutBlockEdit, blockEditSpace],
    activeSpaceId: BLOCK_EDIT_SPACE_ID,
  };
}

/**
 * Exit the active BEDIT session.
 *
 * `action === "save"` leaves the in-flight modifications in place
 * (the BlockDefinition in `document.blocks` already reflects them,
 * because edits in block-edit mode write directly there via the
 * reducer's write-routing wrapper). `action === "discard"` parses the
 * snapshot captured on enter and restores the pre-BEDIT
 * BlockDefinition verbatim — all geometry authored during the session
 * is thrown away.
 *
 * In both cases, the block-edit space is removed from
 * `document.spaces` and the previous active space id is restored. If
 * the previous space no longer exists (rare — e.g. user imported a
 * DXF mid-BEDIT) the model space is the fallback.
 */
export function exitBlockEditSpace(
  document: DrawingDocument,
  action: "save" | "discard",
): DrawingDocument {
  const blockEditSpace = document.spaces.find(
    (space) => space.kind === "block-edit",
  );
  if (!blockEditSpace) {
    // No active BEDIT session — the reducer guards the command
    // upstream but we stay resilient.
    return document;
  }

  const blockName = blockEditSpace.blockEditName;
  const snapshot = blockEditSpace.blockEditSnapshot;
  const previousSpaceId = blockEditSpace.blockEditPreviousSpaceId;

  let nextBlocks = document.blocks;
  if (action === "discard" && typeof snapshot === "string" && blockName) {
    try {
      const restored = JSON.parse(snapshot) as BlockDefinition;
      nextBlocks = { ...document.blocks, [blockName]: restored };
    } catch {
      // Snapshot is corrupt — keep the current definition rather than
      // crashing. The reducer emits a warning audit event.
    }
  }

  const remainingSpaces = document.spaces.filter(
    (space) => space.kind !== "block-edit",
  );

  // Pick the active space: prefer the previous one, fall back to the
  // first model space, fall back to the first remaining space.
  const resolvedActive =
    (previousSpaceId &&
      remainingSpaces.find((space) => space.id === previousSpaceId)?.id) ||
    remainingSpaces.find((space) => space.kind === "model")?.id ||
    remainingSpaces[0]?.id ||
    document.activeSpaceId;

  return {
    ...document,
    blocks: nextBlocks,
    spaces: remainingSpaces,
    activeSpaceId: resolvedActive,
  };
}

/**
 * Update the snapshot captured at enter-time to reflect the current
 * BlockDefinition. Called by BSAVE (quick save) so a subsequent
 * BCLOSE / discard throws away only the changes authored *after* the
 * save, not everything since enter. Returns the document unchanged if
 * no block-edit session is active.
 */
export function refreshBlockEditSnapshot(
  document: DrawingDocument,
): DrawingDocument {
  const blockEditSpace = document.spaces.find(
    (space) => space.kind === "block-edit",
  );
  if (!blockEditSpace || !blockEditSpace.blockEditName) {
    return document;
  }
  const blockDef = document.blocks[blockEditSpace.blockEditName];
  if (!blockDef) {
    return document;
  }
  const updatedSnapshot = JSON.stringify(blockDef);
  return {
    ...document,
    spaces: document.spaces.map((space) =>
      space.kind === "block-edit"
        ? { ...space, blockEditSnapshot: updatedSnapshot }
        : space,
    ),
  };
}

// ═══════════════════════════════════════════════════════════════
// Block-test session helpers (DYN-1.9 — TESTBLOCK / CLOSETESTBLOCK)
// ═══════════════════════════════════════════════════════════════

/**
 * Unique id for the synthetic block-test sandbox space. Like BEDIT we
 * keep a single instance — AutoCAD's TESTBLOCK only runs one preview
 * at a time. Reusing the id keeps UI state (selection, zoom) stable
 * across repeated enter/exit cycles.
 */
export const BLOCK_TEST_SPACE_ID = "__block-test__";

/**
 * Fixed id for the single test INSERT seeded into the block-test
 * space. Consumers (e.g. grip handlers, property palette) reference
 * this id to target the preview instance directly.
 */
export const BLOCK_TEST_INSERT_ID = "__test-insert__";

/**
 * Enter a TESTBLOCK preview for the given block. Mirrors AutoCAD
 * TESTBLOCK: creates a sandbox canvas where a single INSERT of the
 * target block is placed at (0,0) with scale 1 and rotation 0, then
 * activates that canvas so all viewport / grip / property-palette
 * interactions target the preview. The preview INSERT's
 * customProperties drive the evaluator end-to-end — changing a
 * linear parameter through the property palette or a grip re-renders
 * the block entities exactly as they would in model space.
 *
 * The block-test space is transient:
 *   - Not persisted in DXF (filtered by dxfBridge.exportDrawingDocumentToDxfDocument).
 *   - Reset on every enter (a pre-existing block-test space is dropped
 *     before the new one is pushed, matching AutoCAD's single-preview
 *     semantics).
 *   - The previous active space id is captured so CLOSETESTBLOCK can
 *     restore the caller's context (typically the block-edit session
 *     the user came from).
 *
 * Throws if the block does not exist — the reducer guards the command
 * upstream but we stay defensive at the helper layer.
 */
export function enterBlockTestSpace(
  document: DrawingDocument,
  blockName: string,
): DrawingDocument {
  const blockDef = document.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Block "${blockName}" does not exist`);
  }

  const previousSpaceId = document.activeSpaceId;

  // Drop any existing block-test space so repeated TESTBLOCK calls
  // restart the preview cleanly. AutoCAD's TESTBLOCK behaves the same
  // way — there is never more than one preview live at once.
  const spacesWithoutBlockTest = document.spaces.filter(
    (space) => space.kind !== "block-test",
  );

  // Seed the preview INSERT. customProperties / visibilityState stay
  // empty — the evaluator picks up default parameter values from the
  // block definition on the first render pass.
  // Phase 7.2 #38 — replace bare `as DrawingEntity` cast with a
  // schema parse so missing schema-defaulted fields (`layer`, `color`,
  // `linetype`, `lineweight`, `transparency`, `customProperties`,
  // `currentVisibilityState`, …) are filled in canonically rather
  // than producing a partial entity that misleads downstream readers.
  const testInsert: DrawingEntity = blockInsertEntitySchema.parse({
    id: BLOCK_TEST_INSERT_ID,
    spaceId: BLOCK_TEST_SPACE_ID,
    type: "Block",
    blockName,
    insertionPoint: { x: 0, y: 0 },
    scaleX: 1,
    scaleY: 1,
    scaleZ: 1,
    rotation: 0,
    attributes: [],
  });

  const blockTestSpace: DrawingSpace = {
    id: BLOCK_TEST_SPACE_ID,
    kind: "block-test",
    name: `Test Block: ${blockName}`,
    entities: [testInsert],
    // Piggy-back the BEDIT snapshot fields to carry the context we
    // need on exit: which block is being tested and which space was
    // active when TESTBLOCK ran. The `blockEditSnapshot` field is
    // intentionally left undefined — test-block has no snapshot to
    // preserve because no authoring occurs in this space.
    blockEditName: blockName,
    blockEditPreviousSpaceId: previousSpaceId,
    refeditWorkingSet: [],
  };

  return {
    ...document,
    spaces: [...spacesWithoutBlockTest, blockTestSpace],
    activeSpaceId: BLOCK_TEST_SPACE_ID,
  };
}

/**
 * Exit the active TESTBLOCK preview, restoring the previous active
 * space. Discards the block-test space entirely — nothing from the
 * preview interacts with the persistent document state, so there is
 * no "save" variant analogous to BCLOSE save/discard.
 *
 * If no block-test session is active, the document is returned
 * unchanged (resilient no-op).
 */
export function exitBlockTestSpace(document: DrawingDocument): DrawingDocument {
  const blockTestSpace = document.spaces.find(
    (space) => space.kind === "block-test",
  );
  if (!blockTestSpace) {
    return document;
  }

  const previousSpaceId = blockTestSpace.blockEditPreviousSpaceId;
  const remainingSpaces = document.spaces.filter(
    (space) => space.kind !== "block-test",
  );

  const resolvedActive =
    (previousSpaceId &&
      remainingSpaces.find((space) => space.id === previousSpaceId)?.id) ||
    remainingSpaces.find((space) => space.kind === "block-edit")?.id ||
    remainingSpaces.find((space) => space.kind === "model")?.id ||
    remainingSpaces[0]?.id ||
    document.activeSpaceId;

  return {
    ...document,
    spaces: remainingSpaces,
    activeSpaceId: resolvedActive,
  };
}

// ═══════════════════════════════════════════════════════════════
// REFEDIT session helpers (Phase FIN.1 — REFEDIT / REFSET / REFCLOSE)
// ═══════════════════════════════════════════════════════════════

/**
 * Unique id for the synthetic refedit space. Like BEDIT we keep a single
 * instance — AutoCAD's REFEDIT only allows one in-place reference edit
 * at a time. Reusing the id keeps UI state stable across repeated
 * enter/exit cycles.
 */
export const REFEDIT_SPACE_ID = "__refedit__";

/**
 * Enter a REFEDIT session targeting the block referenced by `insertId`
 * inside the currently active space.
 *
 * Mirrors AutoCAD's REFEDIT command:
 *   1. Look up the source INSERT in the active space → derive the block
 *      name. Throws if the INSERT does not exist or does not point at a
 *      defined block.
 *   2. Snapshot the BlockDefinition so a discard exit can restore it.
 *   3. Build a synthetic refedit space whose `entities` array carries
 *      the block's child entities (re-parented to the synthetic space)
 *      AS the working set. The host space's surrounding entities stay
 *      where they are; the renderer queries the active refedit space and
 *      tints them.
 *   4. Activate the synthetic space; the previous active id is captured
 *      for the exit transition.
 */
export function enterRefeditSpace(
  document: DrawingDocument,
  insertId: string,
): DrawingDocument {
  // Locate the INSERT in any space (typically the active one). REFEDIT
  // can be invoked from a layout viewport too; we just need the entity.
  let sourceInsert: BlockInsertEntity | null = null;
  for (const space of document.spaces) {
    for (const entity of space.entities) {
      if (entity.id === insertId && entity.type === "Block") {
        sourceInsert = entity as BlockInsertEntity;
        break;
      }
    }
    if (sourceInsert) break;
  }
  if (!sourceInsert) {
    throw new Error(`INSERT entity "${insertId}" not found`);
  }
  const blockName = sourceInsert.blockName;
  const blockDef = document.blocks[blockName];
  if (!blockDef) {
    throw new Error(`Block "${blockName}" referenced by INSERT does not exist`);
  }

  const previousSpaceId = document.activeSpaceId;
  const snapshot = JSON.stringify(blockDef);

  // Drop any prior refedit space (mirror BEDIT semantics).
  const spacesWithoutRefedit = document.spaces.filter(
    (space) => space.kind !== "refedit",
  );

  // Re-parent block children to the refedit synthetic space so they
  // surface through the regular space.entities iteration paths used by
  // selection, snap, and the renderer. ATTDEF children stay outside the
  // working set — the REFEDIT canvas does not expose them as drawable
  // entities (consistent with how BEDIT treats them).
  const reparented: DrawingEntity[] = blockDef.entities
    .filter(
      (child): child is Exclude<BlockChildEntity, { type: "Attdef" }> =>
        child.type !== "Attdef",
    )
    .map((child) => ({ ...child, spaceId: REFEDIT_SPACE_ID }) as DrawingEntity);

  const workingSet = reparented.map((entity) => entity.id);

  const refeditSpace: DrawingSpace = {
    id: REFEDIT_SPACE_ID,
    kind: "refedit",
    name: `REFEDIT: ${blockName}`,
    entities: reparented,
    refeditBlockName: blockName,
    refeditSourceInsertId: insertId,
    refeditWorkingSet: workingSet,
    refeditSnapshot: snapshot,
    refeditPreviousSpaceId: previousSpaceId,
  };

  return {
    ...document,
    spaces: [...spacesWithoutRefedit, refeditSpace],
    activeSpaceId: REFEDIT_SPACE_ID,
  };
}

/**
 * Exit the active REFEDIT session.
 *
 * `action === "save"` mirrors the working set entities back into the
 * BlockDefinition so every other INSERT of the same block re-renders
 * with the new geometry on the next pass. `action === "discard"`
 * restores the BlockDefinition from the snapshot captured on enter and
 * throws away every authoring change made during the session.
 *
 * In both cases the synthetic refedit space is dropped and the previous
 * active space is restored.
 */
export function exitRefeditSpace(
  document: DrawingDocument,
  action: "save" | "discard",
): DrawingDocument {
  const refeditSpace = document.spaces.find(
    (space) => space.kind === "refedit",
  );
  if (!refeditSpace) return document;

  const blockName = refeditSpace.refeditBlockName;
  const snapshot = refeditSpace.refeditSnapshot;
  const previousSpaceId = refeditSpace.refeditPreviousSpaceId;

  let nextBlocks = document.blocks;

  if (action === "save" && blockName) {
    const existing = document.blocks[blockName];
    if (existing) {
      // Mirror working-set entities back to the block. Preserve any
      // ATTDEF children that lived in the block before REFEDIT — REFEDIT
      // does not expose them, so dropping them silently would corrupt
      // attribute definitions.
      const preservedAttdefs = existing.entities.filter(
        (e) => e.type === "Attdef",
      );
      const workingSetIds = new Set(refeditSpace.refeditWorkingSet);
      const authored: BlockChildEntity[] = [];
      for (const entity of refeditSpace.entities) {
        if (!workingSetIds.has(entity.id)) continue;
        // Strip the spaceId — block children carry it for in-memory
        // consistency only (it's BLOCK-scoped, not space-scoped).
        const { spaceId: _spaceId, ...rest } = entity;
        authored.push(rest as BlockChildEntity);
      }
      nextBlocks = {
        ...document.blocks,
        [blockName]: {
          ...existing,
          entities: [...authored, ...preservedAttdefs],
        },
      };
    }
  } else if (
    action === "discard" &&
    typeof snapshot === "string" &&
    blockName
  ) {
    try {
      const restored = JSON.parse(snapshot) as BlockDefinition;
      nextBlocks = { ...document.blocks, [blockName]: restored };
    } catch {
      // Snapshot is corrupt — keep the current definition rather than
      // crashing. The reducer emits a warning audit event.
    }
  }

  const remainingSpaces = document.spaces.filter(
    (space) => space.kind !== "refedit",
  );

  const resolvedActive =
    (previousSpaceId &&
      remainingSpaces.find((space) => space.id === previousSpaceId)?.id) ||
    remainingSpaces.find((space) => space.kind === "model")?.id ||
    remainingSpaces[0]?.id ||
    document.activeSpaceId;

  return {
    ...document,
    blocks: nextBlocks,
    spaces: remainingSpaces,
    activeSpaceId: resolvedActive,
  };
}

/**
 * Add `entityIds` to the active REFEDIT working set. Entities not yet
 * known to the refedit space (e.g. surrounding entities the user wants
 * to promote into the working set) are not moved here — REFSET in
 * AutoCAD typically operates on entities already located in the refedit
 * canvas; the surrounding-tint to working-set lift requires a higher-
 * level mutation that re-parents the entity from the host space into the
 * refedit space. For Phase FIN.1 we limit the operation to toggling
 * existing space entities into / out of the working-set list. No-op when
 * no REFEDIT session is active.
 */
export function addToRefeditWorkingSet(
  document: DrawingDocument,
  entityIds: string[],
): DrawingDocument {
  if (entityIds.length === 0) return document;
  return {
    ...document,
    spaces: document.spaces.map((space) => {
      if (space.kind !== "refedit") return space;
      const next = new Set(space.refeditWorkingSet);
      for (const id of entityIds) next.add(id);
      return { ...space, refeditWorkingSet: Array.from(next) };
    }),
  };
}

/**
 * Remove `entityIds` from the active REFEDIT working set. Entities that
 * leave the working set continue to live in the refedit space's
 * entities array but are tinted in the renderer and excluded from
 * REFEDIT's persistence path on save (i.e. they revert to their pre-
 * edit form when REFCLOSE save runs). No-op when no REFEDIT session is
 * active.
 */
export function removeFromRefeditWorkingSet(
  document: DrawingDocument,
  entityIds: string[],
): DrawingDocument {
  if (entityIds.length === 0) return document;
  const remove = new Set(entityIds);
  return {
    ...document,
    spaces: document.spaces.map((space) => {
      if (space.kind !== "refedit") return space;
      return {
        ...space,
        refeditWorkingSet: space.refeditWorkingSet.filter(
          (id) => !remove.has(id),
        ),
      };
    }),
  };
}
